/** 결과 — what went out, and what came of it (S8).
 *
 *  Published desk posts and campaign drafts in one table, read from
 *  /api/schedule?status=published. The old Posts page showed only campaign
 *  posts with a bare campaign id and an external id; here each row has the
 *  channel, the title, the link, and — by programme — the desk's request
 *  counts and the closing comment, or the engagement last polled. */

import { useMemo } from 'react'
import { ExternalLink } from 'lucide-react'
import type { ScheduleItem, ScheduleQuery } from '@/api/client'
import { useRedeemApps, useRedeemPosts, useSchedule, useCampaigns } from '@/lib/queries'
import { appLabel } from '@/lib/redeem'
import { cn } from '@/lib/utils'
import {
  DataTable,
  EmptyState,
  FilterBar,
  FilterSelect,
  PageHeader,
  PlatformMark,
  Segmented,
  SourceTag,
  StatTile,
  When,
  platformName,
  useQueryParam,
  type Column,
  type Source,
} from '@/ui'
import { useScheduleFilters } from '../schedule/ScheduleList'
import { channelLabel, headline, ownerName, refOf, sourceOf, useOpenDetail } from '../schedule/shared'

const PERIODS = [7, 30, 90] as const
type Period = (typeof PERIODS)[number]

const PLATFORMS = ['reddit', 'bluesky', 'threads', 'ppomppu', 'okky', 'x', 'instagram', 'tiktok']

/** The latest engagement reading as "likes 3 · replies 1"; `t`, when it was
 *  read, goes to the tooltip. */
export function engagementText(e: ScheduleItem['engagement']): string {
  if (!e) return ''
  return Object.entries(e)
    .filter(([k]) => k !== 't')
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([k, v]) => `${ENGAGEMENT[k] ?? k} ${typeof v === 'number' ? v.toLocaleString() : v}`)
    .join(' · ')
}

/** The keys the pollers write, in Korean; an unknown key shows as it came. */
const ENGAGEMENT: Record<string, string> = {
  views: '조회',
  likes: '좋아요',
  replies: '답글',
  comments: '댓글',
  reposts: '리포스트',
  quotes: '인용',
  bookmarks: '북마크',
  shares: '공유',
  score: '점수',
  upvotes: '추천',
}
const ORDER = ['views', 'likes', 'replies', 'comments', 'reposts', 'quotes', 'bookmarks', 'shares', 'score', 'upvotes']
const rank = (k: string) => (ORDER.includes(k) ? ORDER.indexOf(k) : ORDER.length)

type CloseState = { label: string; tone: 'ok' | 'bad' | 'wait'; title?: string }

export default function Results({ now: nowProp }: { now?: Date }) {
  const now = useMemo(() => nowProp ?? new Date(), [nowProp])
  const filters = useScheduleFilters()
  const [daysParam, setDays] = useQueryParam('days', '30')
  const days: Period = (PERIODS as readonly number[]).includes(Number(daysParam)) ? (Number(daysParam) as Period) : 30
  const open = useOpenDetail()

  const q: ScheduleQuery = {
    from: new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1)).toISOString(),
    to: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString(),
    status: 'published',
    source: filters.source,
    appId: filters.appId || undefined,
    campaignId: filters.campaignId || undefined,
    platform: filters.platform || undefined,
  }
  const feed = useSchedule(q)
  const items = useMemo(
    () => (feed.data?.items ?? []).slice().sort((a, b) => String(b.published_at).localeCompare(String(a.published_at))),
    [feed.data],
  )

  const apps = useRedeemApps().data?.items ?? []
  const campaigns = useCampaigns().data?.items ?? []
  // The closing comment is on the desk post itself, not in the feed.
  const deskPosts = useRedeemPosts(0, 'published').data?.items ?? []
  const closeOf = (it: ScheduleItem): CloseState | null => {
    if (it.kind !== 'redeem') return null
    const post = deskPosts.find((p) => p.id === it.id)
    if (post?.close_error) return { label: '실패', tone: 'bad', title: post.close_error }
    if (post?.closed_at) return { label: '남김', tone: 'ok', title: post.close_url }
    const ended = apps.find((a) => a.app.id === it.app?.id)?.app.ended_at
    return ended ? { label: '대기', tone: 'wait' } : null
  }

  const total = items.reduce(
    (n, it) => ({
      redeem: n.redeem + (it.kind === 'redeem' ? 1 : 0),
      campaign: n.campaign + (it.kind === 'draft' ? 1 : 0),
      requests: n.requests + (it.requests?.total ?? 0),
      pending: n.pending + (it.requests?.pending ?? 0),
      failed: n.failed + (it.requests?.failed ?? 0),
      done: n.done + (it.requests?.done ?? 0),
    }),
    { redeem: 0, campaign: 0, requests: 0, pending: 0, failed: 0, done: 0 },
  )

  const columns: Column<ScheduleItem>[] = [
    {
      key: 'at',
      header: '게시',
      card: 'meta',
      className: 'w-32 whitespace-nowrap text-muted-foreground',
      cell: (it) => <When iso={it.published_at} />,
    },
    {
      key: 'where',
      header: '채널',
      card: 'sub',
      cell: (it) => (
        <div className="flex min-w-0 items-center gap-1.5">
          <SourceTag source={sourceOf(it)} />
          <PlatformMark platform={it.platform} />
          <span className="min-w-0 truncate" title={[channelLabel(it), ownerName(it)].filter(Boolean).join(' · ')}>
            {channelLabel(it)}
            {ownerName(it) && <span className="text-muted-foreground"> · {ownerName(it)}</span>}
          </span>
        </div>
      ),
    },
    {
      key: 'title',
      header: '글',
      card: 'title',
      cell: (it) => <span className="line-clamp-2 break-words font-medium">{headline(it, 80)}</span>,
    },
    {
      key: 'link',
      header: '링크',
      card: 'meta',
      align: 'center',
      width: '56px',
      cell: (it) =>
        it.external_url ? (
          <a
            href={it.external_url}
            target="_blank"
            rel="noreferrer"
            aria-label={`${platformName(it.platform)}에서 글 열기`}
            title={it.external_url}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-accent hover:bg-muted"
          >
            <ExternalLink size={16} aria-hidden />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'requests',
      header: '요청',
      className: 'whitespace-nowrap tabular-nums',
      cardHide: (it) => !it.requests,
      cell: (it) =>
        it.requests ? (
          <span title="전체 · 대기 · 완료 · 실패">
            {it.requests.total}
            <span className="text-muted-foreground">
              {' '}
              · 대기 {it.requests.pending} · 완료 {it.requests.done}
            </span>
            {it.requests.failed > 0 && <span className="text-status-failed"> · 실패 {it.requests.failed}</span>}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'engagement',
      header: '반응',
      className: 'tabular-nums',
      cardHide: (it) => !engagementText(it.engagement),
      cell: (it) => {
        const text = engagementText(it.engagement)
        return text ? (
          <span title={it.engagement?.t ? `읽은 때 ${it.engagement.t}` : undefined}>{text}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      key: 'close',
      header: '종료 댓글',
      className: 'whitespace-nowrap',
      cardHide: (it) => !closeOf(it),
      cell: (it) => {
        const c = closeOf(it)
        if (!c) return <span className="text-muted-foreground">—</span>
        return (
          <span
            title={c.title}
            className={cn(c.tone === 'bad' && 'font-medium text-status-failed', c.tone === 'wait' && 'text-muted-foreground')}
          >
            {c.label}
          </span>
        )
      },
    },
  ]

  return (
    <>
      <PageHeader title="결과" description="올라간 글과 그 뒤 반응. 리딤 글은 요청 수, 캠페인 글은 마지막으로 읽은 반응을 보인다.">
        <FilterBar>
          <Segmented<string>
            label="기간"
            value={String(days)}
            onChange={setDays}
            options={PERIODS.map((p) => ({ value: String(p), label: `${p}일` }))}
          />
          <Segmented<'' | Source>
            label="출처"
            value={filters.source}
            onChange={filters.setSource}
            options={[
              { value: '', label: '전체' },
              { value: 'redeem', label: '리딤' },
              { value: 'campaign', label: '캠페인' },
            ]}
          />
          <FilterSelect
            label="플랫폼"
            value={filters.platform}
            onChange={filters.setPlatform}
            options={[{ value: '', label: '플랫폼 전체' }, ...PLATFORMS.map((p) => ({ value: p, label: platformName(p) }))]}
          />
          {filters.source !== 'campaign' && (
            <FilterSelect
              label="앱"
              value={filters.appId ? String(filters.appId) : ''}
              onChange={filters.setAppId}
              options={[{ value: '', label: '앱 전체' }, ...apps.map((a) => ({ value: String(a.app.id), label: appLabel(a.app, apps.map((x) => x.app)) }))]}
            />
          )}
          {filters.source !== 'redeem' && (
            <FilterSelect
              label="캠페인"
              value={filters.campaignId ? String(filters.campaignId) : ''}
              onChange={filters.setCampaignId}
              options={[{ value: '', label: '캠페인 전체' }, ...campaigns.map((c) => ({ value: String(c.ID), label: c.Name }))]}
            />
          )}
        </FilterBar>
      </PageHeader>

      <div className="mb-4 grid grid-cols-3 gap-2 sm:gap-3">
        <StatTile label="게시" value={items.length} hint={`리딤 ${total.redeem} · 캠페인 ${total.campaign}`} />
        <StatTile
          label="요청"
          value={total.requests}
          hint={
            <>
              대기 {total.pending} · <span className={cn(total.failed > 0 && 'text-status-failed')}>실패 {total.failed}</span>
            </>
          }
          to={total.requests > 0 ? '/redeem/requests' : undefined}
        />
        <StatTile label="발급" value={total.done} hint={`최근 ${days}일 · 코드를 보낸 요청`} />
      </div>

      <DataTable
        label="게시된 글"
        columns={columns}
        rows={feed.isLoading ? undefined : items}
        rowKey={(it) => `${it.kind}-${it.id}`}
        loading={feed.isLoading}
        error={feed.error}
        onRetry={() => void feed.refetch()}
        onRowClick={(it) => open(refOf(it))}
        empty={<EmptyState title={`지난 ${days}일에 올라간 글이 없다`} description="기간을 늘리거나 필터를 풀어 본다." />}
      />
    </>
  )
}
